import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '@/utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      // Emergent Google Auth race-condition fix: if the URL contains
      // #session_id= we are mid OAuth-callback and AuthCallback will
      // handle the exchange. Do NOT call /auth/me here - the cookie
      // isn't set yet and /auth/me would 401, wiping any legacy JWT.
      if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }

      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));

        // Verify token is still valid (works for BOTH transports because
        // /auth/me accepts the session_token cookie OR the Bearer JWT).
        try {
          const currentUser = await authAPI.getCurrentUser();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authAPI.register(userData);
      
      setToken(data.access_token);
      setUser(data.user);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return { success: true, user: data.user };
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string'
        ? detail
        : (detail && typeof detail === 'object' && detail.message)
          || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authAPI.login(credentials);
      
      setToken(data.access_token);
      setUser(data.user);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return { success: true, user: data.user };
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string'
        ? detail
        : (detail && typeof detail === 'object' && detail.message)
          || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refresh = async () => {
    try {
      const data = await authAPI.me();
      setUser(data);
      localStorage.setItem('user', JSON.stringify(data));
      return data;
    } catch (_) {
      return null;
    }
  };

  // Bundle 51 - resume-link handler sets the session directly from a
  // server-minted token (see `GET /api/rsvp/resume/:token`). We stash
  // the token in localStorage under both keys (`token` for legacy code
  // paths, `auth_token` for the axios interceptor) then hydrate the
  // user via /auth/me.
  const setSessionFromToken = async (jwt) => {
    if (!jwt) return null;
    setToken(jwt);
    localStorage.setItem('token', jwt);
    localStorage.setItem('auth_token', jwt);
    localStorage.setItem('access_token', jwt);
    return await refresh();
  };

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    updateUser,
    refresh,
    setSessionFromToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
